import { inject, Injectable } from '@angular/core';
import Bugsnag, { NotifiableError } from '@bugsnag/js';
import { ENV_CONFIG, EnvConfiguration } from '@cumlaude/shared-configuration';
import { RestErrorMessage, RestErrorMessageEnum } from '@cumlaude/service-contract';
import { VERSION } from '@cumlaude/version-generator';

@Injectable({
	providedIn: 'root',
})
export abstract class BugsnagService {
	protected readonly envConfig: EnvConfiguration = inject(ENV_CONFIG);

	notify(error: NotifiableError) {
		console.error(error);

		if (!this.envConfig.bugsnagApiKey) return;

		Bugsnag.notify(error);
	}
}

function safeParseJSON<T>(value: string | undefined | null) {
	if (!value) {
		return null;
	}

	let result: any;
	try {
		result = JSON.parse(value);
	} catch (e) {
		// Invalid JSON
	}
	return result as T;
}

interface FailedRequestInformation {
	url: string;
	statusCode: number;
	response: string;
}

export type AppType = 'User Frontend' | 'Admin Frontend';

/**
 * Een request dat bewust is afgebroken (unsubscribe bij navigatie of filterwissel, `abort()` zonder reden).
 * Een time-out is een `TimeoutError` en een abort met reden heeft een andere boodschap, die blijven gemeld.
 */
function isCancelledRequest(error: { errorClass: string; errorMessage: string }) {
	return error.errorClass === 'AbortError' && error.errorMessage === 'signal is aborted without reason';
}

export function enableBugsnag(envConfig: EnvConfiguration, appType: AppType) {
	if (!envConfig.bugsnagApiKey) return;

	Bugsnag.start({
		apiKey: envConfig.bugsnagApiKey,
		appType: appType,
		appVersion: VERSION || '0.0.0',
		releaseStage: envConfig.environmentName,
		enabledReleaseStages: ['production', 'test', 'acceptance', 'development', 'nightly', 'lokaal'],
		collectUserIp: false,
		onError: (event) => {
			event.clearMetadata('angular', 'component');
			event.clearMetadata('angular', 'context');
			if (event.request.url) event.request.url = stripFiltersFrontend(event.request.url);
			event.errors.forEach((error) => (error.errorMessage = stripFiltersBackend(error.errorMessage)));

			if ((event.originalError as RestErrorMessage | undefined)?.error === RestErrorMessageEnum.ABORTED) return false;
			if (event.errors.some(isCancelledRequest)) return false;

			return !event.errors.some((error) => safeParseJSON<FailedRequestInformation>(error.errorMessage)?.statusCode === 401);
		},
		onBreadcrumb: (breadcrumb) => {
			switch (breadcrumb.type) {
				case 'request':
					breadcrumb.metadata['url'] = stripFiltersBackend(breadcrumb.metadata['url']);
					break;
				case 'navigation':
					if (breadcrumb.metadata['to']) breadcrumb.metadata['to'] = stripFiltersFrontend(breadcrumb.metadata['to']);
					if (breadcrumb.metadata['from']) breadcrumb.metadata['from'] = stripFiltersFrontend(breadcrumb.metadata['from']);
					break;
			}
			return true;
		},
	});
}

const allowlist = [
	'g',
	'aanwezigheiduitsplitsing',
	'aspect',
	'afwijkingpercentiel',
	'brin-overgang',
	'categorieuitstroomniveauvso',
	'cijfersseceweergave',
	'cijfertype',
	'chat',
	'cohortrendementtype',
	'cohortrendementweergave',
	'col',
	'doorstroomweergave',
	'dossier',
	'details-list',
	'eenheid',
	'indicator-over',
	'inspectie-data',
	'interval',
	'kenmerk',
	'lesuur-maand',
	'lockedColumn',
	'or-uitsluiten',
	'reden-maand',
	'selectie',
	'schooljaar',
	'sortOrder',
	'sidebarSortOrder',
	'snapshot',
	'tekortpunten',
	'tijdseenheid',
	'threshold',
	'toon-trend',
	'tussentijdseuitstroomvso',
	'uitstroomniveauvso',
	'vakOrder',
	'variant',
	'verschilbeoogd',
];

function stripFiltersFrontend(input: string) {
	const [path, query] = input.split('?');
	if (!query) return path;

	const redacted = query
		.split('&')
		.map((kv) => {
			const [key, val] = kv.split('=');
			if (allowlist.includes(key)) return `${key}=${val}`;
			else return `${key}=REDACTED`;
		})
		.join('&');

	return `${path}?${redacted}`;
}

function stripFiltersBackend(input: string) {
	return input
		.replaceAll(/\bf=[^&]*/g, 'f=REDACTED')
		.replaceAll(/\bof=[^&]*/g, 'of=REDACTED')
		.replaceAll(/\bpf=[^&]*/g, 'pf=REDACTED');
}
